import { Component, Output, EventEmitter } from '@angular/core';

import { Observable } from 'rxjs';
import { PrTimeToReviewCalculatorService } from '../../services/pr-time-to-review-calculator.service';
import { TeamMetricsService } from '../../services/team-metrics.service';
import { PrTimeToFirstReviewResult, CalculationLogic } from '../../models/team-metrics';
import { MetricCardComponent } from '../metric-card/metric-card.component';
import { BaseMetricWithCalculationLogicComponent } from './base-metric-with-calculation-logic.component';

@Component({
  selector: 'app-pr-time-to-review-metric',
  standalone: true,
  imports: [MetricCardComponent],
  template: `
    <app-metric-card
      title="PR Time to First Review"
      icon="schedule"
      [metricData]="metricDataToDisplay"
      metricType="prTime"
      [isLoading]="isLoading"
      tooltipText="Time pull requests created in the selected period waited for their first non-bot comment/vote. Calculation method selected in filter bar. Outliers shown in subtitle.">
    </app-metric-card>
  `
})
/**
 * Component to display the PR Time to First Review metric.
 * It extends BaseMetricWithCalculationLogicComponent to handle selectable calculation logic
 * (Mean, Median, IQR) and leverages common metric functionalities.
 * This component also emits its loading state.
 */
export class PrTimeToReviewMetricComponent extends BaseMetricWithCalculationLogicComponent<PrTimeToFirstReviewResult> {
  /**
   * Emits the loading state of this specific metric component.
   * This can be used by parent components to coordinate loading indicators.
   */
  @Output() prtLoadingState = new EventEmitter<boolean>();

  /**
   * Constructs the PrTimeToReviewMetricComponent.
   * @param prTimeCalculatorService Service to calculate PR Time to First Review.
   * @param teamMetricsService Service for shared team metrics functionalities.
   */
  constructor(
    private readonly prTimeCalculatorService: PrTimeToReviewCalculatorService,
    teamMetricsService: TeamMetricsService
  ) {
    super(teamMetricsService);
    
    this.loadingStateChange.subscribe(isLoading => {
      this.prtLoadingState.emit(isLoading);
    });
  }

  /**
   * Fetches the PR Time to First Review metric data using the specified calculation logic.
   * This method is called by the base component to retrieve metric data.
   * @param team The team name for which to calculate the metric.
   * @param isoStartDate The start date of the period in ISO format.
   * @param isoEndDate The end date of the period in ISO format.
   * @param calculationLogic The calculation logic to apply (Mean, Median, IQR).
   * @param org The ADO organization name. 
   * @returns An Observable emitting the PrTimeToFirstReviewResult or null.
   */
  protected callFetchMetricWithCalculationLogic(
    team: string, 
    isoStartDate: string, 
    isoEndDate: string,
    calculationLogic: CalculationLogic,
    org: string,
    _project: string
  ): Observable<PrTimeToFirstReviewResult | null> {
    return this.prTimeCalculatorService.calculate(team, isoStartDate, isoEndDate, org, calculationLogic, true);
  }

  /**
   * Determines if the metric can be recalculated with a new logic using existing raw data.
   * Requires raw durations and the original period dates to be present in the retrieved data.
   * @returns True if recalculation is possible, false otherwise.
   */
  protected canRecalculate(): boolean {
    const data = this.retrievedData;
    return !!(data?.rawDurationsHours && data.startDateIso && data.endDateIso);
  }

  /**
   * Recalculates the PR Time to First Review metric using a new calculation logic
   * based on previously fetched raw data. This avoids making another API call.
   * It rebuilds the current period's result and, if comparison data exists,
   * recalculates the previous period's result and the percentage change.
   * @param newLogic The new calculation logic (Mean, Median, IQR) to apply.
   */
  protected recalculateMetric(newLogic: CalculationLogic): void {
    const oldData = this.retrievedData as PrTimeToFirstReviewResult;
    
    this.isLoading = true;
    this.emitLoadingState(true);
    
    const recalculatedCurrent = this.prTimeCalculatorService.buildPrTimeToFirstReviewResult(
      oldData.rawDurationsHours ?? [],
      oldData.totalPrsConsidered,
      oldData.totalPrsFetched,
      oldData.startDateIso ?? '',
      oldData.endDateIso ?? '',
      newLogic
    );

    let newPreviousValue: number | undefined = undefined;
    let newPercentageChange: number | undefined = undefined;
    const existingComparisonPeriod = oldData.comparisonPeriod;
    const prevPeriodData = oldData.previousPeriodDataForRecalc; 

    if (prevPeriodData?.rawDurationsHours && prevPeriodData?.startDateIso && prevPeriodData?.endDateIso) {
      const knownRawDurations = prevPeriodData.rawDurationsHours;
      const knownStartDate: string = prevPeriodData.startDateIso;
      const knownEndDate: string = prevPeriodData.endDateIso;
      
      const recalculatedPrevious = this.prTimeCalculatorService.buildPrTimeToFirstReviewResult(
        knownRawDurations, 
        prevPeriodData.totalPrsConsidered,
        prevPeriodData.totalPrsFetched,
        knownStartDate,
        knownEndDate,
        newLogic
      );
      newPreviousValue = recalculatedPrevious.value;
      if (typeof recalculatedCurrent.value === 'number' && typeof newPreviousValue === 'number') {
         if (newPreviousValue !== 0) {
            newPercentageChange = ((recalculatedCurrent.value - newPreviousValue) / newPreviousValue) * 100;
        } else if (recalculatedCurrent.value > 0) {
            newPercentageChange = 100;
        } else {
            newPercentageChange = 0;
        }
      }
    }
    
    const recalculatedData: PrTimeToFirstReviewResult = {
      ...recalculatedCurrent,
      previousValue: newPreviousValue,
      percentageChange: newPercentageChange,
      comparisonPeriod: existingComparisonPeriod,
      previousPeriodDataForRecalc: prevPeriodData
    };
    
    this.retrievedData = recalculatedData;
    this.isLoading = false;
    this.emitLoadingState(false);
    this.trySetCardData();
  }
}
